/**
 * GeoProposalScorecard
 *
 * Rules (all scoring uses only what the reader enters):
 * - The reader can compare up to 3 agency proposals.
 * - An agency is "in use" once it has a name or at least one answer.
 * - Each of the 6 questions is answered Yes (2 points), Partly (1 point) or No (0 points).
 *   Maximum score is 12. The score is only shown once all 6 questions are answered.
 * - Any ticked red flag marks that proposal as "flagged", whatever its score.
 * - Recommendation: among fully answered, unflagged proposals, the highest score leads.
 *   Ties are reported as ties. If none qualify, the reader is pointed to a free audit.
 * - No tracking, storage, cookies or network calls.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";

type Answer = "yes" | "partly" | "no";

const QUESTIONS: string[] = [
  "Shows a real AI citation report from a law firm client",
  "Names the schema and attorney markup planned for the first 90 days",
  "Has a named person checking accuracy and bar advertising compliance",
  "Confirms it does not represent a competitor in your practice area and market",
  "Confirms you own the content, schema and data if you leave",
  "Explains how AI-sourced leads will be tagged at intake",
];

const RED_FLAGS: string[] = [
  "Guarantees AI citations or a timeline like \"in ChatGPT within 30 days\"",
  "Offers no citation tracking",
  "Pitches GEO as a replacement for SEO",
  "Keeps ownership of your content or data",
  "Publishes legal content without attorney review",
  "Works with a direct competitor and has no written conflict policy",
];

const ANSWER_LABELS: { value: Answer; label: string }[] = [
  { value: "yes", label: "Yes" },
  { value: "partly", label: "Partly" },
  { value: "no", label: "No" },
];

const POINTS: Record<Answer, number> = { yes: 2, partly: 1, no: 0 };
const MAX_SCORE = QUESTIONS.length * 2;

interface Proposal {
  name: string;
  answers: (Answer | null)[];
  flags: boolean[];
}

const emptyProposal = (): Proposal => ({
  name: "",
  answers: QUESTIONS.map(() => null),
  flags: RED_FLAGS.map(() => false),
});

export default function GeoProposalScorecard() {
  const [proposals, setProposals] = useState<Proposal[]>([
    emptyProposal(),
    emptyProposal(),
    emptyProposal(),
  ]);

  const update = (index: number, change: (p: Proposal) => Proposal) =>
    setProposals((prev) => prev.map((p, i) => (i === index ? change(p) : p)));

  const label = (p: Proposal, i: number) => p.name.trim() || `Agency ${i + 1}`;

  const summaries = proposals.map((p, i) => {
    const answered = p.answers.filter((a) => a !== null).length;
    const inUse = p.name.trim() !== "" || answered > 0;
    const complete = answered === QUESTIONS.length;
    const score = p.answers.reduce((sum, a) => sum + (a ? POINTS[a] : 0), 0);
    const fullYes = p.answers.filter((a) => a === "yes").length;
    const flagCount = p.flags.filter(Boolean).length;
    return { index: i, name: label(p, i), inUse, complete, answered, score, fullYes, flagCount };
  });

  const active = summaries.filter((s) => s.inUse);
  const qualifying = active.filter((s) => s.complete && s.flagCount === 0);
  const best = qualifying.length ? Math.max(...qualifying.map((s) => s.score)) : -1;
  const leaders = qualifying.filter((s) => s.score === best);

  return (
    <section className="not-prose my-8 rounded-lg border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">GEO proposal scorecard</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Score up to three agency proposals on the same six questions, then tick any red flags you
        spotted. Nothing you enter is saved or sent anywhere.
      </p>

      <div className="mt-6 grid gap-6 xl:grid-cols-3">
        {proposals.map((p, pi) => (
          <fieldset key={pi} className="min-w-0 rounded-lg border border-border p-4">
            <legend className="max-w-full px-1 text-sm font-semibold break-words">{label(p, pi)}</legend>

            <label htmlFor={`geo-agency-${pi}`} className="block text-sm font-medium">
              Agency name
            </label>
            <input
              id={`geo-agency-${pi}`}
              type="text"
              value={p.name}
              placeholder="e.g. Agency A"
              onChange={(e) => update(pi, (prev) => ({ ...prev, name: e.target.value }))}
              className="mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />

            <ol className="mt-4 space-y-4">
              {QUESTIONS.map((q, qi) => (
                <li key={qi}>
                  <p id={`geo-q-${pi}-${qi}`} className="text-sm">
                    {qi + 1}. {q}
                  </p>
                  <div role="group" aria-labelledby={`geo-q-${pi}-${qi}`} className="mt-2 flex gap-2">
                    {ANSWER_LABELS.map((opt) => {
                      const selected = p.answers[qi] === opt.value;
                      return (
                        <Button variant={selected ? "default" : "outline"}
                          key={opt.value}
                          type="button"
                          aria-pressed={selected}
                          onClick={() =>
                            update(pi, (prev) => ({
                              ...prev,
                              answers: prev.answers.map((a, ai) =>
                                ai === qi ? (selected ? null : opt.value) : a,
                              ),
                            }))
                          }
                          className={`h-auto whitespace-normal rounded-md border px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                            selected
                              ? "border-border bg-primary text-primary-foreground"
                              : "border-input bg-card text-card-foreground"
                          }`}
                        >
                          {opt.label}
                        </Button>
                      );
                    })}
                  </div>
                </li>
              ))}
            </ol>

            <p className="mt-5 text-sm font-medium">Red flags you noticed</p>
            <ul className="mt-2 space-y-2">
              {RED_FLAGS.map((flag, fi) => (
                <li key={fi} className="flex items-start gap-2">
                  <input
                    id={`geo-flag-${pi}-${fi}`}
                    type="checkbox"
                    checked={p.flags[fi]}
                    onChange={(e) =>
                      update(pi, (prev) => ({
                        ...prev,
                        flags: prev.flags.map((f, i) => (i === fi ? e.target.checked : f)),
                      }))
                    }
                    className="mt-1 h-4 w-4 rounded border-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  <label htmlFor={`geo-flag-${pi}-${fi}`} className="text-sm text-muted-foreground">
                    {flag}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        ))}
      </div>

      <div aria-live="polite" className="mt-6 rounded-lg bg-muted p-4 text-sm">
        {active.length === 0 ? (
          <p className="text-muted-foreground">
            Start by naming an agency or answering a question. Results appear here.
          </p>
        ) : (
          <>
            <ul className="space-y-2">
              {active.map((s) => (
                <li key={s.index}>
                  <span className="font-semibold">{s.name}: </span>
                  {s.complete
                    ? `${s.score} of ${MAX_SCORE} points, with ${s.fullYes} of ${QUESTIONS.length} questions answered in full.`
                    : `${s.answered} of ${QUESTIONS.length} questions answered so far.`}
                  {s.flagCount > 0 &&
                    ` ${s.flagCount} red flag${s.flagCount > 1 ? "s" : ""} ticked, so treat this proposal with caution.`}
                </li>
              ))}
            </ul>

            <p className="mt-4">
              {leaders.length === 1 &&
                `${leaders[0].name} answers your questions most completely with no red flags. Ask it to put the named deliverables and the reporting format in the contract.`}
              {leaders.length > 1 &&
                `${leaders.map((l) => l.name).join(" and ")} are tied. Compare their exclusivity terms and a sample monthly report to separate them.`}
              {leaders.length === 0 &&
                active.some((s) => s.complete) &&
                "None of the fully scored proposals is free of red flags. Get a second opinion before you sign anything."}
              {leaders.length === 0 &&
                !active.some((s) => s.complete) &&
                "Answer all six questions for at least one agency to see a recommendation."}
            </p>

            <p className="mt-3">
              Want a neutral baseline to compare proposals against?{" "}
              <Link
                to="/law-firm-seo-audit"
                className="font-medium underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Get a free law firm SEO audit
              </Link>
              .
            </p>
          </>
        )}
      </div>
    </section>
  );
}
